import axios from "axios";
import { BASE_URL, PERMISSION_URL } from "../config";
import { logout, setupGlobalAxiosInterceptor } from "./userService";
import { encryptionStore } from "../store/encryptionStore";
import { encrypt, decrypt } from "../utils/crypto";
import { attachTracingHeaders } from "../utils/tracing";

// Setup global axios interceptor for direct axios calls
setupGlobalAxiosInterceptor();

// Create an Axios instance with default headers for BASE_URL
const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Axios instance without default Content-Type for multipart/form-data
const formDataAxiosInstance = axios.create({
  baseURL: BASE_URL,
});

// Create an Axios instance with default headers for PERMISSION_URL
const permissionAxiosInstance = axios.create({
  baseURL: PERMISSION_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Axios instance without auth header (for public/partner endpoints)
const publicAxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Create an Axios instance with default headers for PERMISSION_URL
const imageAxiosInstance = axios.create({
  baseURL: PERMISSION_URL,
  headers: {
    accept: "*/*",
  },
});

// Interceptor to add Authorization token to requests
const addAuthToken = (config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

// Interceptor to add Authorization token to requests
const noAuthToken = (config) => {
  return config;
};

function isMultipartBody(data) {
  return (
    (typeof FormData !== "undefined" && data instanceof FormData) ||
    (typeof Blob !== "undefined" && data instanceof Blob) ||
    (typeof File !== "undefined" && data instanceof File)
  );
}

const addEncryption = async (config) => {
  if (isMultipartBody(config.data)) {
    if (config.headers) {
      delete config.headers["Content-Type"];
    }
  } else if (encryptionStore.isEnabled) {
    const url = config.url || "";
    const baseURL = config.baseURL || "";
    const fullUrl = baseURL + url;
    const isUpm = typeof window !== 'undefined' && window.location.hostname.includes("upm");
    
    if (!fullUrl.includes("/auth/encryption-config") && !fullUrl.includes("upm") && !isUpm) {
      config.headers["X-Encrypted"] = "true";

      if (config.data) {
        try {
          const plaintext = typeof config.data === "string" ? config.data : JSON.stringify(config.data);
          const { ciphertext, timeslot } = await encrypt(plaintext);
          config.data = { encrypted: ciphertext };
          config.headers["X-Enc-Ts"] = timeslot;
        } catch (err) {
          console.error("[Encryption] Failed to encrypt request:", err);
        }
      }
    }
  }
  return config;
};

// Request interceptors
axiosInstance.interceptors.request.use(attachTracingHeaders, (error) => Promise.reject(error));
formDataAxiosInstance.interceptors.request.use(attachTracingHeaders, (error) => Promise.reject(error));
publicAxiosInstance.interceptors.request.use(attachTracingHeaders, (error) => Promise.reject(error));
// Do not attach tracing headers to permissionAxiosInstance / imageAxiosInstance.
// They communicate with external UPM whose CORS policy rejects non-whitelisted custom headers.

axiosInstance.interceptors.request.use(addEncryption, (error) => Promise.reject(error));
formDataAxiosInstance.interceptors.request.use(addEncryption, (error) => Promise.reject(error));
publicAxiosInstance.interceptors.request.use(addEncryption, (error) => Promise.reject(error));

axiosInstance.interceptors.request.use(addAuthToken, (error) => Promise.reject(error));
formDataAxiosInstance.interceptors.request.use(addAuthToken, (error) => Promise.reject(error));
permissionAxiosInstance.interceptors.request.use(noAuthToken, (error) => Promise.reject(error));
imageAxiosInstance.interceptors.request.use(noAuthToken, (error) => Promise.reject(error));
publicAxiosInstance.interceptors.request.use(noAuthToken, (error) => Promise.reject(error));

// Function to show 403 error message
const show403Error = (endpoint) => {
  // Extract endpoint name from URL
  let apiEndpoint = endpoint || 'Unknown endpoint';
  
  // If it's a full URL, extract just the endpoint path
  if (apiEndpoint.includes('http')) {
    try {
      const url = new URL(apiEndpoint);
      apiEndpoint = url.pathname;
    } catch (e) {
      // If URL parsing fails, try to extract from string
      const match = apiEndpoint.match(/\/(api\/[^?]+)/);
      if (match) {
        apiEndpoint = match[1];
      }
    }
  }
  
  // Remove leading slash if present
  apiEndpoint = apiEndpoint.replace(/^\//, '');
  
  const message = `Access to this endpoint is not allowed: ${apiEndpoint}`;
  
  // Dispatch a custom event that can be listened to globally
  const event = new CustomEvent('api403Error', {
    detail: { message }
  });
  window.dispatchEvent(event);
  
  // Also log to console
  console.error(`403 Forbidden: ${message}`);
};

const shouldSkip401Logout = (error) => Boolean(error?.config?.skip401Logout);

const decryptResponse = async (response) => {
  const isEncrypted = response.headers?.["x-encrypted"] === "true";
  const encTs = response.headers?.["x-enc-ts"];

  if (isEncrypted && response.data?.encrypted && encTs) {
    try {
      const decryptedStr = await decrypt(response.data.encrypted, encTs);
      try {
        response.data = JSON.parse(decryptedStr);
      } catch (parseErr) {
        // If it's not JSON (e.g., HTML), use the raw string
        response.data = decryptedStr;
      }
    } catch (err) {
      console.error("[Encryption] Failed to decrypt response:", err);
    }
  }
  return response;
};

const decryptError = async (error) => {
  const encTs = error.response?.headers?.["x-enc-ts"];
  if (error.response?.headers?.["x-encrypted"] === "true" && error.response?.data?.encrypted && encTs) {
    try {
      const decryptedStr = await decrypt(error.response.data.encrypted, encTs);
      try {
        error.response.data = JSON.parse(decryptedStr);
      } catch (parseErr) {
        error.response.data = decryptedStr;
      }
    } catch (err) {
      // ignore
    }
  }
  return Promise.reject(error);
};

// Response interceptors for decryption (added first so they run first on response)
axiosInstance.interceptors.response.use(decryptResponse, decryptError);
publicAxiosInstance.interceptors.response.use(decryptResponse, decryptError);
formDataAxiosInstance.interceptors.response.use(decryptResponse, decryptError);
permissionAxiosInstance.interceptors.response.use(decryptResponse, decryptError);

// Existing response interceptors
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      if (shouldSkip401Logout(error)) {
        console.warn('[API] 401 Unauthorized - not logging out for this request');
      } else {
        // Logout user on 401 Unauthorized
        console.warn('[API] 401 Unauthorized - Logging out user');
        logout();
      }
      return Promise.reject(error);
    }
    if (error?.response?.status === 403) {
      const endpoint = error?.config?.url || error?.request?.responseURL || error?.config?.baseURL + error?.config?.url || 'Unknown endpoint';
      show403Error(endpoint);
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

publicAxiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401 || error?.response?.status === 403) {
      // No auth expected here; just propagate
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

formDataAxiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      if (shouldSkip401Logout(error)) {
        console.warn('[FormData API] 401 Unauthorized - not logging out for this request');
      } else {
        // Logout user on 401 Unauthorized
        console.warn('[FormData API] 401 Unauthorized - Logging out user');
        logout();
      }
      return Promise.reject(error);
    }
    if (error?.response?.status === 403) {
      const endpoint = error?.config?.url || error?.request?.responseURL || error?.config?.baseURL + error?.config?.url || 'Unknown endpoint';
      show403Error(endpoint);
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

permissionAxiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      if (shouldSkip401Logout(error)) {
        console.warn('[Permission API] 401 Unauthorized - not logging out for this request');
      } else {
        // Logout user on 401 Unauthorized (even for permission API)
        console.warn('[Permission API] 401 Unauthorized - Logging out user');
        logout();
      }
      return Promise.reject(error);
    }
    if (error?.response?.status === 403) {
      const endpoint = error?.config?.url || error?.request?.responseURL || error?.config?.baseURL + error?.config?.url || 'Unknown endpoint';
      show403Error(endpoint);
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

imageAxiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      if (shouldSkip401Logout(error)) {
        console.warn('[Image API] 401 Unauthorized - not logging out for this request');
      } else {
        // Logout user on 401 Unauthorized (even for image API)
        console.warn('[Image API] 401 Unauthorized - Logging out user');
        logout();
      }
      return Promise.reject(error);
    }
    if (error?.response?.status === 403) {
      const endpoint = error?.config?.url || error?.request?.responseURL || error?.config?.baseURL + error?.config?.url || 'Unknown endpoint';
      show403Error(endpoint);
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

// POST request function
const post = async (apiEndpoint, payload, config = {}) => {
  try {
    // Route FormData payloads through the instance without default Content-Type
    if (payload instanceof FormData) {
      const res = await formDataAxiosInstance.post(apiEndpoint, payload, config);
      return res;
    }
    const res = await axiosInstance.post(apiEndpoint, payload, config);
    return res;
  } catch (error) {
    throw error?.response || error;
  }
};

// POST without Authorization header (for public/partner endpoints)
const postPublic = async (apiEndpoint, payload, config = {}) => {
  try {
    const res = await publicAxiosInstance.post(apiEndpoint, payload, config);
    return res;
  } catch (error) {
    throw error?.response || error;
  }
};

// GET request function
const get = async (apiEndpoint, config = {}) => {
  try {
    const res = await axiosInstance.get(apiEndpoint, config);
    return res;
  } catch (error) {
    throw error?.response || error;
  }
};

// PUT request function
const put = async (apiEndpoint, payload, config = {}) => {
  try {
    const res = await axiosInstance.put(apiEndpoint, payload, config);
    return res;
  } catch (error) {
    throw error?.response || error;
  }
};

// DELETE request function
const del = async (apiEndpoint) => {
  try {
    const res = await axiosInstance.delete(apiEndpoint);
    return res;
  } catch (error) {
    throw error?.response || error;
  }
};

// PATCH request function
const patch = async (apiEndpoint, payload) => {
  try {
    const res = await axiosInstance.patch(apiEndpoint, payload);
    return res;
  } catch (error) {
    throw error?.response || error;
  }
};

// PERMISSION POST request function
const permission = async (apiEndpoint, payload, config = {}) => {
  try {
    const res = await permissionAxiosInstance.post(apiEndpoint, payload, config);
    return res;
  } catch (error) {
    throw error?.response || error;
  }
};

// IMAGE upload POST request function
const imageUpload = async (apiEndpoint, payload, config = {}) => {
  try {
    const res = await imageAxiosInstance.post(apiEndpoint, payload, config);
    return res;
  } catch (error) {
    throw error?.response || error;
  }
};

// Exporting the client
export const client = { post, postPublic, get, put, del, patch, permission, imageUpload };
export default client;
